/**
 * JSON-LD for the issue viewer, rendered during SSR so crawlers receive it in the served HTML.
 * Mirrors what `IssuesReactHelper#get_structured_data_content` builds for the Relay stack, but
 * reads the route's page query instead of re-querying the issue.
 */
export type IssueSeoSchemaProps = {
  title: string
  body: string
  url: string
  createdAt: string
  /** Omitted until `totalCommentsCount` can be selected without a re-pin (github/github-ui#30860). */
  commentCount?: number
  author?: {login: string; profileUrl: string | null} | null
}

export function IssueSeoSchema({title, body, url, createdAt, commentCount, author}: IssueSeoSchemaProps) {
  // The Relay stack omits the tag entirely when the issue has no author, so match that.
  if (!author) return null

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'DiscussionForumPosting',
    headline: title,
    articleBody: body,
    author: {
      url: author.profileUrl,
      '@type': 'Person',
      name: author.login,
    },
    datePublished: createdAt,
    ...(commentCount === undefined
      ? {}
      : {
          interactionStatistic: {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/CommentAction',
            userInteractionCount: commentCount,
          },
        }),
    url,
  }

  // Titles and bodies are user-controlled: escape `<` so a `</script>` cannot break out of the tag.
  const jsonLd = JSON.stringify(structuredData).replace(/</g, '\\u003c')

  /* eslint-disable-next-line react/no-danger */
  return <script type="application/ld+json" dangerouslySetInnerHTML={{__html: jsonLd}} />
}
